import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH } from "../constants";

/**
 * useFreezePanes
 * Owns frozen-rows/frozen-cols state plus the per-row/per-col sticky
 * offsets used by the cell renderer.  Offsets are computed in two passes:
 *   1. A synchronous "seed" from rowHeights / colWidths so the first
 *      paint is approximately correct (no zero-stack flash).
 *   2. A pixel-exact refinement inside a useLayoutEffect that measures
 *      the real DOM positions via getBoundingClientRect (rounded to
 *      whole pixels) and replaces the seeds.
 *
 * Returns refs the parent must hand off:
 *   - `gridWrapperRef` → attached to the scrollable wrapper around the table.
 *   - `theadRef`       → attached to the thead element.
 */
export default function useFreezePanes({
  activeSheet, sheetDataByName,
  frozenInit, rowHeights, colWidths,
  hiddenRows, hiddenCols, isRowVisible,
  normalizedRowsLength, zoomScale,
}) {
  const freezeBySheetRef = useRef({});
  const [frozenRows, setFrozenRows] = useState(frozenInit?.rows || 0);
  const [frozenCols, setFrozenCols] = useState(frozenInit?.cols || 0);
  const [freezeCtx, setFreezeCtx] = useState(null); // { x, y, row, col }

  // Sync freeze state on sheet switch — restore the user override if any,
  // otherwise read the freeze pane defaults the parser captured.
  useEffect(() => {
    if (!activeSheet) return;
    const saved = freezeBySheetRef.current[activeSheet];
    if (saved) {
      setFrozenRows(saved.rows);
      setFrozenCols(saved.cols);
    } else {
      const model = sheetDataByName[activeSheet];
      setFrozenRows(model?.frozenRows || 0);
      setFrozenCols(model?.frozenCols || 0);
    }
  }, [activeSheet, sheetDataByName]);

  const handleFreeze = useCallback((rows, cols) => {
    setFrozenRows(rows);
    setFrozenCols(cols);
    if (activeSheet) freezeBySheetRef.current[activeSheet] = { rows, cols };
    setFreezeCtx(null);
  }, [activeSheet]);

  const handleUnfreeze = useCallback(() => handleFreeze(0, 0), [handleFreeze]);

  const handleCellContextMenu = useCallback((e, rowIndex, colIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setFreezeCtx({ x: e.clientX, y: e.clientY, row: rowIndex, col: colIndex });
  }, []);

  // --- Sticky offsets ---
  const gridWrapperRef = useRef(null);
  const theadRef = useRef(null);
  const [measuredTheadHeight, setMeasuredTheadHeight] = useState(24);

  const seedRowOffsets = useMemo(() => {
    if (frozenRows <= 0) return [];
    const offsets = new Array(frozenRows);
    let acc = 0;
    for (let r = 0; r < frozenRows; r++) {
      offsets[r] = acc;
      const visible = r === 0 ? true : (!hiddenRows.has(r) && isRowVisible(r));
      if (visible) acc += (rowHeights[r] || DEFAULT_ROW_HEIGHT);
    }
    return offsets;
  }, [frozenRows, rowHeights, hiddenRows, isRowVisible]);

  const seedColOffsets = useMemo(() => {
    if (frozenCols <= 0) return [];
    const offsets = new Array(frozenCols);
    let acc = HEADER_COL_WIDTH;
    for (let c = 0; c < frozenCols; c++) {
      offsets[c] = acc;
      if (!hiddenCols.has(c)) acc += (colWidths[c] || DEFAULT_COL_WIDTH);
    }
    return offsets;
  }, [frozenCols, colWidths, hiddenCols]);

  const [frozenRowOffsets, setFrozenRowOffsets] = useState(seedRowOffsets);
  const [frozenColOffsets, setFrozenColOffsets] = useState(seedColOffsets);

  useEffect(() => { setFrozenRowOffsets(seedRowOffsets); }, [seedRowOffsets]);
  useEffect(() => { setFrozenColOffsets(seedColOffsets); }, [seedColOffsets]);

  // Dynamic header measurement inside a useLayoutEffect.
  // The row and column offsets rely purely on mathematically exact cumulative
  // JS heights/widths (seedRowOffsets / seedColOffsets) to prevent subpixel
  // DOM rounding errors that create transparent visual gaps.
  useLayoutEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!wrapper) return;
    const table = wrapper.querySelector("table.simple-excel-grid");
    if (!table) return;
    const thead = theadRef.current || table.querySelector("thead");
    if (!thead) return;

    const theadRect = thead.getBoundingClientRect();
    setMeasuredTheadHeight(Math.round(theadRect.height));
  }, [frozenRows, frozenCols, colWidths, rowHeights, hiddenCols, hiddenRows,
      activeSheet, normalizedRowsLength, zoomScale, isRowVisible]);

  return {
    frozenRows, frozenCols,
    handleFreeze, handleUnfreeze,
    freezeCtx, setFreezeCtx, handleCellContextMenu,
    gridWrapperRef, theadRef,
    measuredTheadHeight, frozenRowOffsets, frozenColOffsets,
  };
}
